import React from "react";
import { Prompt, useClonePrompt, useDeletePrompt } from "@/lib/prompts-store";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Edit3, Copy, Trash2, Shield, Users, Globe, Play } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
interface PromptTableProps {
  prompts: Prompt[];
  onEdit: (prompt: Prompt) => void;
  onOpenPlayground: (prompt: Prompt) => void;
}
export function PromptTable({ prompts, onEdit, onOpenPlayground }: PromptTableProps) {
  const clonePrompt = useClonePrompt();
  const deletePrompt = useDeletePrompt();
  const visibilityIcons = {
    private: <Shield className="w-3 h-3" />,
    team: <Users className="w-3 h-3" />,
    public: <Globe className="w-3 h-3" />,
  };
  return (
    <div className="rounded-xl border border-white/5 bg-card/50 overflow-hidden">
      <Table>
        <TableHeader className="bg-muted/30">
          <TableRow>
            <TableHead className="w-[40%]">Prompt</TableHead>
            <TableHead>Visibility</TableHead>
            <TableHead>Model</TableHead>
            <TableHead>Usage</TableHead>
            <TableHead>Success Rate</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {prompts.map((prompt) => (
            <TableRow key={prompt.id} className="hover:bg-muted/20">
              <TableCell>
                <div className="font-bold text-sm">{prompt.name}</div>
                <div className="text-xs text-muted-foreground line-clamp-1 mt-1">{prompt.content}</div>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="capitalize text-[10px] gap-1.5">
                  {visibilityIcons[prompt.visibility]}
                  {prompt.visibility}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="text-xs font-mono">{prompt.model}</div>
                <div className="text-[10px] text-muted-foreground">T: {prompt.temperature}</div>
              </TableCell>
              <TableCell>
                <div className="text-sm font-bold">{prompt.metrics.usageCount}</div>
                <div className="text-[10px] text-muted-foreground">{prompt.metrics.avgTokens} tokens/run</div>
              </TableCell>
              <TableCell>
                <span className={`text-sm font-bold ${prompt.metrics.successRate > 95 ? 'text-green-500' : 'text-amber-500'}`}>
                  {prompt.metrics.successRate}%
                </span>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onOpenPlayground(prompt)}>
                    <Play className="w-4 h-4" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEdit(prompt)} className="gap-2"><Edit3 className="w-4 h-4" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => clonePrompt(prompt.id)} className="gap-2"><Copy className="w-4 h-4" /> Fork</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => deletePrompt(prompt.id)} className="text-destructive gap-2"><Trash2 className="w-4 h-4" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
